/** Source files stay hidden; face crops are opened individually from a gallery. */
import type { GraphEdge, GraphNode } from '@/components/neo4j/cytoscape/types'

type EvidenceNode = Pick<GraphNode, 'id' | 'labels' | 'typeLabel'>

export function isFaceNode(node: EvidenceNode): boolean {
  return node.typeLabel === 'Face' || Boolean(node.labels?.includes('Face'))
}

/**
 * The face ids a view may show: none while Advanced Face Detection is off
 * (ADR-0051), so saved face evidence stays hidden until it is back on.
 */
export function shownFaceIds(
  expandedFaceIds: readonly string[] | undefined,
  faceDetection: boolean
): readonly string[] {
  return faceDetection ? (expandedFaceIds ?? []) : []
}

/** Every Face node, and every edge touching one, dropped; the rest untouched (list views). */
export function withoutFaceNodes<Node extends EvidenceNode, Edge extends GraphEdge>(
  nodes: Node[],
  edges: Edge[]
): { nodes: Node[]; edges: Edge[] } {
  const faceIds = new Set(nodes.filter(isFaceNode).map(node => node.id))
  if (faceIds.size === 0) return { nodes, edges }
  return {
    nodes: nodes.filter(node => !faceIds.has(node.id)),
    edges: edges.filter(edge => !faceIds.has(edge.source) && !faceIds.has(edge.target)),
  }
}

export interface EvidenceScope {
  /** The graph view: source files and unexpanded faces hidden. A list shows the rest. */
  graphOnly: boolean
  expandedFaceIds?: readonly string[]
  /** Advanced Face Detection is on; off hides every face, in the graph and in lists. */
  faceDetection: boolean
}

/** What an investigation shows of its saved graph, in its graph view or in a list. */
export function investigationEvidence<Node extends EvidenceNode, Edge extends GraphEdge>(
  nodes: Node[],
  edges: Edge[],
  scope: EvidenceScope
): { nodes: Node[]; edges: Edge[] } {
  if (scope.graphOnly) {
    return filterFaceEvidence(
      nodes,
      edges,
      shownFaceIds(scope.expandedFaceIds, scope.faceDetection)
    )
  }
  return scope.faceDetection ? { nodes, edges } : withoutFaceNodes(nodes, edges)
}

/** Also applies to old saved views that already contain every face. */
export function filterFaceEvidence<Node extends EvidenceNode, Edge extends GraphEdge>(
  nodes: Node[],
  edges: Edge[],
  expandedFaceIds: readonly string[] = []
): { nodes: Node[]; edges: Edge[] } {
  const expanded = new Set(expandedFaceIds)
  const visibleNodes = nodes.filter(
    node =>
      node.typeLabel !== 'Document' &&
      !node.labels?.includes('Document') &&
      node.typeLabel !== 'Voiceprint' &&
      !node.labels?.includes('Voiceprint') &&
      (!isFaceNode(node) || expanded.has(node.id))
  )
  const visibleIds = new Set(visibleNodes.map(node => node.id))
  const voiceIds = new Set(
    visibleNodes
      .filter(node => node.typeLabel === 'Voice' || node.labels?.includes('Voice'))
      .map(node => node.id)
  )
  return {
    nodes: visibleNodes,
    edges: edges.filter(
      edge =>
        edge.type !== 'EXTRACTED_FROM' &&
        visibleIds.has(edge.source) &&
        visibleIds.has(edge.target) &&
        (edge.type !== 'DEPICTS' || edge.properties?.status === 'confirmed') &&
        (edge.type !== 'VOICE_OF' ||
          !voiceIds.has(edge.source) ||
          edge.properties?.status === 'confirmed')
    ),
  }
}
